import {
  GetSubmissionResponse,
  SubmissionStatusFilter,
} from '@commenda/api/exemption-portal/types';
import {
  CertificateTypeEnum,
  ExemptionIdTypeEnum,
  ExemptionReasonEnum,
} from '@commenda/api/new-sales-tax/exemptions/types';
import {
  ExemptionPortalHelper,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { State } from 'country-state-city';
import {
  CERTIFICATE_TYPE_OPTIONS,
  EXEMPTION_REASON_NAME_MAP,
} from '../constants';

export interface ISerializedCertificate {
  id: string | number;
  sellerName: string;
  customerName: string;
  stateCodes: string[];
  stateNames: string[];
  exemptionReason: string;
  certificateType: CertificateTypeEnum;
  certificateTypeDisplay: string;
  exemptionReasonDisplay: string;
  expirationDate: string | null;
  expirationDateDisplay: string;
  submittedDate: string;
  statusCode: SubmissionStatusFilter | null;
  statusDisplay: string;
  statusBadgeVariant: string;
  originalSubmission: GetSubmissionResponse;
  derivedStatus: SubmissionStatusFilter | null;
  derivedStatusDisplay: string;
  derivedStatusBadgeVariant: string;
}

export interface CertificateFormData {
  type: CertificateTypeEnum;
  reason: ExemptionReasonEnum;
  effective_date: string;
  expiration_date: string;
  jurisdictions: {
    state: string;
    country: string;
    identification_type: ExemptionIdTypeEnum;
    identification_number: string;
  }[];
}

export const serializeCertificate = (
  submission: GetSubmissionResponse
): ISerializedCertificate => {
  const submissionFormData: any = submission?.formData;

  let stateCodes: string[] = [];
  let stateNames: string[] = [];

  if (submissionFormData?.jurisdictions) {
    // New format with jurisdictions array
    stateCodes = submissionFormData.jurisdictions.map((j: any) => j.state);
    stateNames = stateCodes.map(
      (code: string) => State.getStateByCodeAndCountry(code, 'US')?.name || '-'
    );
  }

  const certificateType = submissionFormData?.type || '';
  const certificateTypeDisplay = CERTIFICATE_TYPE_OPTIONS.find(
    (option) => option.value.toString() === certificateType?.toString()
  )?.label;

  const expirationDate = submission.expiresAt || null;
  const expirationDateDisplay = expirationDate
    ? NonTimezoneSensitiveDate.fromNativeDate(new Date(expirationDate)).format(
        'MMM D, YYYY'
      )
    : "Doesn't expire";

  const submittedDate = submission.updatedAt || '';
  const submittedDateDisplay = submittedDate
    ? NonTimezoneSensitiveDate.fromNativeDate(new Date(submittedDate)).format(
        'MMM D, YYYY'
      )
    : '-';

  const exemptionReason = submissionFormData?.reason || '';
  const exemptionReasonDisplay = exemptionReason
    ? EXEMPTION_REASON_NAME_MAP[exemptionReason] || '-'
    : '-';

  const derivedStatus =
    (submission?.derivedStatus as SubmissionStatusFilter) || null;
  const derivedStatusDisplay = derivedStatus
    ? ExemptionPortalHelper.DERIVED_STATUS_NAME_MAP[derivedStatus] || '-'
    : '-';

  const statusCode = submission?.status as SubmissionStatusFilter;
  const statusDisplay = statusCode
    ? ExemptionPortalHelper.DERIVED_STATUS_NAME_MAP[statusCode] || '-'
    : '-';

  return {
    id: submission.id,
    certificateType,
    certificateTypeDisplay,
    sellerName: submission?.customerSellerRelationship?.sellerName || '-',
    customerName: submission?.customerSellerRelationship?.customerName || '-',
    stateCodes,
    stateNames,
    exemptionReason,
    exemptionReasonDisplay,
    expirationDate,
    expirationDateDisplay,
    submittedDate: submittedDateDisplay,
    statusCode,
    statusDisplay,
    derivedStatus,
    derivedStatusDisplay,
    statusBadgeVariant:
      ExemptionPortalHelper.getSubmissionStatusBadgeVariant(statusCode),
    derivedStatusBadgeVariant:
      ExemptionPortalHelper.getSubmissionStatusBadgeVariant(derivedStatus),
    originalSubmission: submission,
  };
};

export const serializeCertificates = (
  submissions: GetSubmissionResponse[]
): ISerializedCertificate[] => {
  return submissions.map(serializeCertificate);
};
